/**
 * LA - Deletion Request Form
 * Handles form submission with backend API checking on page load
 * Uses /clients/delete_request/check endpoint for duplicate detection
 * 
 * @version 1.2.0
 * @changelog
 * - Changed: Status now displays as a styled card instead of modifying rich-text section
 * - Changed: Original page heading and description are preserved
 * - Fixed: Submitted date now reflects actual submission date from backend
 * - Added: New status card UI matching design specifications
 */

(function() {
  'use strict';

  // State management
  const state = {
    isSubmitting: false,
    isCheckingBackend: false,
    hasExistingRequest: false,
    form: null,
    showStatusEnabled: true,
    debugMode: false,
    submitButton: null,
    originalButtonText: ''
  };

  // ========================================
  // DEBUG LOGGING HELPER
  // ========================================

  function log(...args) {
    if (state.debugMode) {
      console.log('[DeletionForm]', ...args);
    }
  }

  function logError(...args) {
    if (state.debugMode) {
      console.error('[DeletionForm ERROR]', ...args);
    }
  }

  function logWarn(...args) {
    if (state.debugMode) {
      console.warn('[DeletionForm WARN]', ...args);
    }
  }

  // ========================================
  // SUBMIT BUTTON MANAGEMENT
  // ========================================

  function lockSubmitButton(text = 'Processing...') {
    if (!state.submitButton) return;
    
    state.submitButton.disabled = true;
    state.submitButton.textContent = text;
    state.submitButton.style.opacity = '0.5';
    state.submitButton.style.cursor = 'not-allowed';
    state.submitButton.style.pointerEvents = 'none';
  }

  function unlockSubmitButton() {
    if (!state.submitButton) return;
    
    const termsCheckbox = state.form?.querySelector('input[name="terms_acknowledged"]');
    const isTermsChecked = termsCheckbox?.checked ?? false;
    
    state.submitButton.disabled = !isTermsChecked;
    state.submitButton.textContent = state.originalButtonText;
    state.submitButton.style.opacity = isTermsChecked ? '1' : '0.5';
    state.submitButton.style.cursor = isTermsChecked ? 'pointer' : 'not-allowed';
    state.submitButton.style.pointerEvents = 'auto';
  }

  function updateSubmitButtonState() {
    if (!state.submitButton || !state.form) return;
    
    // Never unlock if we're submitting or have existing request
    if (state.isSubmitting || state.hasExistingRequest) {
      lockSubmitButton(state.isSubmitting ? 'Submitting...' : 'Request Already Submitted');
      return;
    }
    
    const termsCheckbox = state.form.querySelector('input[name="terms_acknowledged"]');
    if (termsCheckbox) {
      state.submitButton.disabled = !termsCheckbox.checked;
      state.submitButton.style.opacity = termsCheckbox.checked ? '1' : '0.5';
      state.submitButton.style.cursor = termsCheckbox.checked ? 'pointer' : 'not-allowed';
    }
  }

  // ========================================
  // BACKEND API CHECK
  // ========================================

  /**
   * Check if user already has a pending deletion request via backend API
   * Uses /clients/delete_request/check endpoint
   * @param {string} email - User's email to check
   * @returns {Object|null} - { exists: true, submitted_at: '...' } or null
   */
  async function checkForExistingRequest(email) {
    if (!email) {
      log('No email provided for check');
      return null;
    }
    
    if (state.isCheckingBackend) {
      log('Already checking backend, skipping...');
      return null;
    }

    state.isCheckingBackend = true;

    try {
      log('=== CHECKING BACKEND API FOR EXISTING REQUEST ===');
      log('Email to check:', email);
      
      // Build the check endpoint URL
      const checkEndpoint = window.ENV_CONFIG?.getProxyUrl('/clients/delete_request/check') || '/apps/api/clients/delete_request/check';

      log('→ Check endpoint:', checkEndpoint);

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

      const response = await fetch(checkEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ email: email.toLowerCase().trim() }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      log('← Response status:', response.status);

      if (!response.ok) {
        throw new Error(`Backend API returned ${response.status}`);
      }

      const result = await response.json();
      log('← Response data:', result);

      // Backend returns { status: 1, message: "Request already exists", submitted_at: "..." } if duplicate
      // Backend returns { status: 0 } if no existing request
      if (result.status === 1) {
        log('✓ Found existing request for this user');
        state.hasExistingRequest = true;
        
        // Use submitted_at from backend response, with fallbacks
        const submittedDate = result.submitted_at || result.submitted_date || result.created_at || result.date || null;
        log('→ Submitted date from backend:', submittedDate);
        
        return {
          exists: true,
          email: email,
          status: 'pending',
          submitted_at: submittedDate
        };
      }

      log('✓ No existing request found for this user');
      state.hasExistingRequest = false;
      return null;

    } catch (error) {
      if (error.name === 'AbortError') {
        logError('✗ Backend API request timed out');
      } else {
        logError('✗ Error checking Backend API:', error);
      }
      // On error, don't change hasExistingRequest state - allow form submission
      // The backend will catch duplicates during actual submission
      return null;
      
    } finally {
      state.isCheckingBackend = false;
    }
  }

  // ========================================
  // LOADER FUNCTIONS
  // ========================================

  function showCheckingLoader() {
    if (!state.form) return;

    const existingLoader = document.querySelector('.deletion-form__checking-loader');
    if (existingLoader) return; // Already showing

    const loadingOverlay = document.createElement('div');
    loadingOverlay.className = 'deletion-form__checking-loader';
    loadingOverlay.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(255, 255, 255, 0.9);
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      z-index: 100;
      border-radius: 8px;
    `;

    loadingOverlay.innerHTML = `
      <div style="
        width: 30px;
        height: 30px;
        border: 3px solid #f3f3f3;
        border-top: 3px solid #63308a;
        border-radius: 50%;
        animation: deletionFormSpin 1s linear infinite;
        margin-bottom: 1rem;
      "></div>
      <p style="color: #616264; margin: 0; font-size: 14px;">Checking your account status...</p>
      <style>
        @keyframes deletionFormSpin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      </style>
    `;

    const wrapper = state.form.closest('.deletion-form__wrapper');
    if (wrapper) {
      wrapper.style.position = 'relative';
      wrapper.appendChild(loadingOverlay);
    }
  }

  function hideCheckingLoader() {
    const loader = document.querySelector('.deletion-form__checking-loader');
    if (loader) {
      loader.remove();
    }
  }

  // ========================================
  // STATUS CARD DISPLAY
  // ========================================

  /**
   * Show the status card in place of the form
   * This creates a styled card matching the design specifications
   * @param {Object} submission - The submission data with submitted_at date
   */
  function showStatusCard(submission) {
    log('=== SHOWING STATUS CARD ===');
    log('→ Submission data:', submission);
    
    // Format date if available
    let dateDisplay = '';
    if (submission && submission.submitted_at) {
      const submittedDate = new Date(submission.submitted_at).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
      dateDisplay = `Submitted on ${submittedDate}`;
      log('→ Formatted date:', dateDisplay);
    } else {
      log('→ No submitted_at date provided, skipping date display');
    }

    // Get customizable text from data attributes
    const pendingHeading = state.form?.dataset.pendingHeading || 'Request received';
    const pendingDescription = state.form?.dataset.pendingDescription || "We'll process your request to delete your account soon.\nYou'll receive an email confirmation once your account is removed.";
    const pendingHelpText = state.form?.dataset.pendingHelpText || 'Need Help?';
    const pendingHelpUrl = state.form?.dataset.pendingHelpUrl || '/pages/contact';

    // Create the status card HTML
    const statusCardHTML = `
      <div class="deletion-status-card">
        <h2 class="deletion-status-card__heading">${pendingHeading}</h2>
        ${dateDisplay ? `<p class="deletion-status-card__date">${dateDisplay}</p>` : ''}
        <p class="deletion-status-card__description">${pendingDescription.replace(/\n/g, '<br>')}</p>
        <p class="deletion-status-card__contact">Questions? <a href="mailto:hello@lovealways.com">Email us</a>.</p>
      </div>
      <p class="deletion-status-card__help-link"><a href="${pendingHelpUrl}">${pendingHelpText}</a></p>
    `;

    // Find the form wrapper and replace form with status card
    const formWrapper = state.form?.closest('.deletion-form__wrapper');
    
    if (formWrapper) {
      // Hide the form
      if (state.form) {
        state.form.style.display = 'none';
      }
      
      // Check if status card already exists
      const existingCard = formWrapper.querySelector('.deletion-status-card');
      if (existingCard) {
        log('→ Status card already exists, skipping creation');
        return;
      }
      
      // Insert the status card after the form (or at the end of wrapper)
      const statusContainer = document.createElement('div');
      statusContainer.className = 'deletion-status-card-container';
      statusContainer.innerHTML = statusCardHTML;
      formWrapper.appendChild(statusContainer);
      
      log('✓ Status card created and inserted');
    } else {
      logError('✗ Could not find form wrapper to insert status card');
    }
  }

  /**
   * Hide only the form element
   */
  function hideForm() {
    if (state.form) {
      state.form.style.display = 'none';
      log('✓ Form hidden');
    }
  }

  /**
   * Hide form completely without showing status
   */
  function hideFormAndStatus() {
    hideForm();
    
    // Also hide the entire deletion form section
    const formSection = document.querySelector('.deletion-form');
    if (formSection) {
      formSection.style.display = 'none';
      log('✓ Form section hidden');
    }
    
    log('✓ Form completely hidden (show status is disabled)');
  }

  // ========================================
  // MODAL FUNCTIONS
  // ========================================

  function removeAllModals() {
    ['loading-modal', 'success-modal', 'error-modal', 'duplicate-modal'].forEach(id => {
      const modal = document.getElementById(id);
      if (modal) modal.remove();
    });
  }

  function showLoadingModal() {
    removeAllModals();

    const modalHTML = `
      <div id="loading-modal" style="
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(0, 0, 0, 0.5);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 10000;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      ">
        <div style="
          background: white;
          padding: 40px;
          border-radius: 12px;
          text-align: center;
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
          animation: deletionModalSlideIn 0.3s ease-out;
        ">
          <div style="
            width: 40px;
            height: 40px;
            border: 4px solid #f3f3f3;
            border-top: 4px solid #dc3545;
            border-radius: 50%;
            animation: deletionModalSpin 1s linear infinite;
            margin: 0 auto 20px;
          "></div>
          <h3 style="
            color: #374151;
            font-size: 20px;
            font-weight: 600;
            margin-bottom: 10px;
          ">Processing Your Request</h3>
          <p style="
            color: #6B7280;
            font-size: 16px;
            margin: 0;
          ">Please wait while we submit your deletion request...</p>
        </div>
      </div>
      <style>
        @keyframes deletionModalSlideIn {
          from { opacity: 0; transform: translateY(-20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes deletionModalSpin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      </style>
    `;

    document.body.insertAdjacentHTML('beforeend', modalHTML);
  }

  function showSuccessModal() {
    removeAllModals();

    const modalHTML = `
      <div id="success-modal" style="
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(0, 0, 0, 0.5);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 10000;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      ">
        <div style="
          background: white;
          padding: 40px;
          border-radius: 12px;
          max-width: 493px;
          width: 90%;
          text-align: center;
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
          position: relative;
          animation: deletionModalSlideIn 0.3s ease-out;
        ">
          <button id="close-success-modal" style="
            position: absolute;
            top: 15px;
            right: 15px;
            background: none;
            border: none;
            font-size: 24px;
            cursor: pointer;
            color: #666;
            padding: 5px;
            line-height: 1;
          ">&times;</button>
          <div style="
            width: 60px;
            height: 60px;
            border-radius: 50%;
            background-color: #d4edda;
            margin: 0 auto 20px;
            display: flex;
            align-items: center;
            justify-content: center;
          ">
            <span style="
              color: #28a745;
              font-size: 30px;
              font-weight: bold;
            ">✓</span>
          </div>
          <h2 style="
            font-family: var(--font-body);
            color: #28a745;
            font-size: 24px;
            font-weight: 600;
            margin-bottom: 20px;
            line-height: 1.3;
          ">Request Received</h2>
          <p style="
            color: #303132;
            font-size: 16px;
            line-height: 1.5;
            margin-bottom: 20px;
          ">
          We'll process your request to delete your account soon. You'll receive an email confirmation once your account is removed.
          <br><br>
          Questions? <a href="mailto:hello@lovealways.com">Email us</a>.
          </p>
          <p style="
            color: #303132;
            font-size: 16px;
            line-height: 1.5;
            margin-bottom: 0;
          ">Thank you for letting us know.</p>
        </div>
      </div>
    `;

    document.body.insertAdjacentHTML('beforeend', modalHTML);

    const modal = document.getElementById('success-modal');
    const closeBtn = document.getElementById('close-success-modal');

    const closeModal = () => {
      if (modal) modal.remove();
    };

    closeBtn?.addEventListener('click', closeModal);
    modal?.addEventListener('click', (e) => {
      if (e.target === modal) closeModal();
    });
    
    const escHandler = (e) => {
      if (e.key === 'Escape' && document.getElementById('success-modal')) {
        closeModal();
        document.removeEventListener('keydown', escHandler);
      }
    };
    document.addEventListener('keydown', escHandler);
  }

  function showDuplicateModal() {
    removeAllModals();

    const modalHTML = `
      <div id="duplicate-modal" style="
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(0, 0, 0, 0.5);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 10000;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      ">
        <div style="
          background: white;
          padding: 40px;
          border-radius: 12px;
          max-width: 450px;
          width: 90%;
          text-align: center;
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
          position: relative;
          animation: deletionModalSlideIn 0.3s ease-out;
          border-top: 4px solid #f59e0b;
        ">
          <button id="close-duplicate-modal" style="
            position: absolute;
            top: 15px;
            right: 15px;
            background: none;
            border: none;
            font-size: 24px;
            cursor: pointer;
            color: #666;
            padding: 5px;
            line-height: 1;
          ">&times;</button>
          <div style="
            width: 60px;
            height: 60px;
            border-radius: 50%;
            background-color: #fef3c7;
            margin: 0 auto 20px;
            display: flex;
            align-items: center;
            justify-content: center;
          ">
            <span style="
              color: #f59e0b;
              font-size: 30px;
              font-weight: bold;
            ">!</span>
          </div>
          <h2 style="
            color: #92400e;
            font-size: 22px;
            font-weight: 600;
            margin-bottom: 15px;
          ">Request Already Submitted</h2>
          <p style="
            color: #374151;
            font-size: 16px;
            line-height: 1.5;
            margin-bottom: 20px;
          ">You have already submitted an account deletion request. Our team is processing it and you'll receive an email confirmation once complete.</p>
          <button id="duplicate-ok-btn" style="
            background-color: #f59e0b;
            color: white;
            border: none;
            padding: 12px 24px;
            border-radius: 6px;
            font-size: 16px;
            font-weight: 500;
            cursor: pointer;
            transition: background-color 0.2s;
          ">
            Okay, Got It
          </button>
        </div>
      </div>
    `;

    document.body.insertAdjacentHTML('beforeend', modalHTML);

    const modal = document.getElementById('duplicate-modal');
    const closeBtn = document.getElementById('close-duplicate-modal');
    const okBtn = document.getElementById('duplicate-ok-btn');

    const closeModal = () => {
      if (modal) modal.remove();
    };

    closeBtn?.addEventListener('click', closeModal);
    okBtn?.addEventListener('click', closeModal);
    modal?.addEventListener('click', (e) => {
      if (e.target === modal) closeModal();
    });

    const escHandler = (e) => {
      if (e.key === 'Escape' && document.getElementById('duplicate-modal')) {
        closeModal();
        document.removeEventListener('keydown', escHandler);
      }
    };
    document.addEventListener('keydown', escHandler);
  }

  function showErrorModal(errorMessage, errors = []) {
    removeAllModals();

    let errorListHTML = '';
    if (errors.length > 0) {
      errorListHTML = '<ul style="text-align: left; margin: 20px 0; padding-left: 20px; color: #dc2626;">';
      errors.forEach(error => {
        errorListHTML += `<li style="margin-bottom: 8px;"><strong>${error.field}:</strong> ${error.message}</li>`;
      });
      errorListHTML += '</ul>';
    }

    const modalHTML = `
      <div id="error-modal" style="
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(0, 0, 0, 0.5);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 10000;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      ">
        <div style="
          background: white;
          padding: 40px;
          border-radius: 12px;
          max-width: 450px;
          width: 90%;
          text-align: center;
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
          position: relative;
          animation: deletionModalSlideIn 0.3s ease-out;
          border-top: 4px solid #dc2626;
        ">
          <button id="close-error-modal" style="
            position: absolute;
            top: 15px;
            right: 15px;
            background: none;
            border: none;
            font-size: 24px;
            cursor: pointer;
            color: #666;
            padding: 5px;
            line-height: 1;
          ">&times;</button>
          <div style="
            width: 60px;
            height: 60px;
            border-radius: 50%;
            background-color: #fee2e2;
            margin: 0 auto 20px;
            display: flex;
            align-items: center;
            justify-content: center;
          ">
            <span style="
              color: #dc2626;
              font-size: 30px;
              font-weight: bold;
            ">!</span>
          </div>
          <h2 style="
            color: #dc2626;
            font-size: 22px;
            font-weight: 600;
            margin-bottom: 15px;
          ">Submission Error</h2>
          <p style="
            color: #374151;
            font-size: 16px;
            line-height: 1.5;
            margin-bottom: 10px;
          ">${errorMessage}</p>
          ${errorListHTML}
          <button id="try-again-btn" style="
            background-color: #dc2626;
            color: white;
            border: none;
            padding: 12px 24px;
            border-radius: 6px;
            font-size: 16px;
            font-weight: 500;
            cursor: pointer;
            margin-top: 10px;
            transition: background-color 0.2s;
          ">
            Try Again
          </button>
        </div>
      </div>
    `;

    document.body.insertAdjacentHTML('beforeend', modalHTML);

    const modal = document.getElementById('error-modal');
    const closeBtn = document.getElementById('close-error-modal');
    const tryAgainBtn = document.getElementById('try-again-btn');

    const closeModal = () => {
      if (modal) modal.remove();
    };

    closeBtn?.addEventListener('click', closeModal);
    tryAgainBtn?.addEventListener('click', closeModal);
    modal?.addEventListener('click', (e) => {
      if (e.target === modal) closeModal();
    });

    const escHandler = (e) => {
      if (e.key === 'Escape' && document.getElementById('error-modal')) {
        closeModal();
        document.removeEventListener('keydown', escHandler);
      }
    };
    document.addEventListener('keydown', escHandler);
  }

  // ========================================
  // VALIDATION FUNCTIONS
  // ========================================

  function validateForm() {
    const errors = [];

    const emailInput = state.form.querySelector('input[name="email"]');
    if (emailInput) {
      const email = emailInput.value.trim();
      if (!email) {
        errors.push({ field: 'Email', message: 'This field is required' });
        addFieldError('email');
      } else {
        const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
        if (!emailRegex.test(email)) {
          errors.push({ field: 'Email', message: 'Please enter a valid email address' });
          addFieldError('email');
        }
      }
    }

    const reasonTextarea = state.form.querySelector('textarea[name="reason"]');
    if (reasonTextarea) {
      const reason = reasonTextarea.value.trim();
      if (reason.length > 2000) {
        errors.push({ field: 'Reason', message: 'Maximum 2000 characters allowed' });
        addFieldError('reason');
      }
    }

    const termsCheckbox = state.form.querySelector('input[name="terms_acknowledged"]');
    if (termsCheckbox && !termsCheckbox.checked) {
      errors.push({ field: 'Terms Agreement', message: 'You must acknowledge the terms to proceed' });
      addFieldError('terms_acknowledged');
    }

    return errors;
  }

  function addFieldError(fieldName) {
    const field = state.form.querySelector(`[name="${fieldName}"]`);
    if (!field) return;

    field.style.border = '2px solid #dc2626';
    field.style.boxShadow = '0 0 0 3px rgba(220, 38, 38, 0.1)';
    field.classList.add('form-field-error');

    if (!document.querySelector('#deletion-form-error-styles')) {
      const styles = document.createElement('style');
      styles.id = 'deletion-form-error-styles';
      styles.textContent = `
        .form-field-error {
          animation: deletionFieldShake 0.5s ease-in-out;
        }
        @keyframes deletionFieldShake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
          20%, 40%, 60%, 80% { transform: translateX(5px); }
        }
      `;
      document.head.appendChild(styles);
    }

    setTimeout(() => field.classList.remove('form-field-error'), 500);
  }

  function removeFieldError(fieldName) {
    const field = state.form.querySelector(`[name="${fieldName}"]`);
    if (!field) return;

    field.style.border = '';
    field.style.boxShadow = '';
    field.classList.remove('form-field-error');
  }

  // ========================================
  // FORM HANDLING
  // ========================================

  function clearForm() {
    if (!state.form) return;

    const reasonTextarea = state.form.querySelector('textarea[name="reason"]');
    if (reasonTextarea) {
      reasonTextarea.value = '';
      removeFieldError('reason');
      const counter = state.form.querySelector('.character-count');
      if (counter) counter.textContent = '0/2000';
    }

    const termsCheckbox = state.form.querySelector('input[name="terms_acknowledged"]');
    if (termsCheckbox) {
      termsCheckbox.checked = false;
      removeFieldError('terms_acknowledged');
    }

    updateSubmitButtonState();
  }

  async function handleFormSubmit(e) {
    e.preventDefault();

    // Prevent double submission
    if (state.isSubmitting) {
      log('Form submission already in progress - ignoring');
      return;
    }

    // Check if already has existing request (from initial load)
    if (state.hasExistingRequest) {
      log('User already has existing request - showing duplicate modal');
      showDuplicateModal();
      return;
    }

    log('=== STARTING FORM SUBMISSION ===');
    state.isSubmitting = true;
    lockSubmitButton('Submitting...');

    // Client-side validation
    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      log('✗ Client-side validation failed');
      showErrorModal('Please correct the following errors:', validationErrors);
      state.isSubmitting = false;
      unlockSubmitButton();
      return;
    }

    // Clear any existing field errors
    ['email', 'reason', 'terms_acknowledged'].forEach(field => removeFieldError(field));

    const email = state.form.querySelector('input[name="email"]').value.trim();
    const reason = state.form.querySelector('textarea[name="reason"]')?.value || '';

    // PRE-SUBMISSION CHECK: Verify no pending request exists right before submitting
    log('→ Running pre-submission duplicate check...');
    showLoadingModal();

    try {
      const existingRequest = await checkForExistingRequest(email);
      
      if (existingRequest && existingRequest.exists) {
        log('✗ Pre-submission check found existing request - blocking submission');
        state.hasExistingRequest = true;
        removeAllModals();
        showDuplicateModal();
        
        // After showing duplicate modal, update UI to show status card
        setTimeout(() => {
          if (state.showStatusEnabled) {
            showStatusCard(existingRequest);
          } else {
            hideFormAndStatus();
          }
        }, 2000);
        
        return;
      }

      log('✓ Pre-submission check passed - no existing request found');

    } catch (checkError) {
      logWarn('Pre-submission check failed, proceeding with submission:', checkError);
      // Continue with submission even if check fails - backend will catch duplicates
    }

    // Proceed with submission
    try {
      const termsCheckbox = state.form.querySelector('input[name="terms_acknowledged"]');
      const termsAcknowledged = termsCheckbox?.checked ? 'true' : 'false';
      const shop = state.form.dataset.shop;
      const returnTo = state.form.dataset.returnTo;

      const payload = {
        email: email,
        reason: reason,
        terms_acknowledged: termsAcknowledged,
        shop: shop,
        return_to: returnTo
      };

      // Build full endpoint URL
      const endpointPath = state.form.dataset.endpointPath || '/clients/delete-request';
      const endpoint = window.ENV_CONFIG?.getProxyUrl(endpointPath) || endpointPath;

      log('→ Environment:', window.ENV_CONFIG?.environment);
      log('→ Endpoint Path:', endpointPath);
      log('→ Full Endpoint:', endpoint);

      log('→ Submitting to:', endpoint);
      log('→ Payload:', payload);

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      log('← Server response status:', response.status);

      let result = {};
      try {
        result = await response.json();
        log('← Server response data:', result);
      } catch (jsonError) {
        logWarn('Could not parse JSON response');
      }

      // Check for duplicate submission response from backend
      // Backend returns { status: 1, message: "Request already exists" } for duplicates
      if (result.status === 1 && result.message && result.message.toLowerCase().includes('already exists')) {
        log('🔄 Backend reports duplicate - showing duplicate modal');
        state.hasExistingRequest = true;
        
        // Use submitted_at from backend response if available
        const submission = {
          email: email,
          status: 'pending',
          submitted_at: result.submitted_at || result.submitted_date || result.created_at || null,
          reason: reason
        };
        
        removeAllModals();
        showDuplicateModal();

        setTimeout(() => {
          const modal = document.getElementById('duplicate-modal');
          if (modal) modal.remove();
          
          if (state.showStatusEnabled) {
            showStatusCard(submission);
          } else {
            hideFormAndStatus();
          }
        }, 3000);
        return;
      }

      // Success
      if (response.ok || result.success) {
        log('✓ FORM SUBMISSION SUCCESSFUL');
        state.hasExistingRequest = true;

        // For new submissions, use current time as the actual submission time
        const submission = {
          email: email,
          status: 'pending',
          submitted_at: new Date().toISOString(),
          reason: reason
        };

        clearForm();
        removeAllModals();
        showSuccessModal();

        // Show status card after modal closes
        setTimeout(() => {
          const modal = document.getElementById('success-modal');
          if (modal) modal.remove();
          
          log('→ Checking if should show status...');
          log('→ state.showStatusEnabled:', state.showStatusEnabled);
          
          if (state.showStatusEnabled) {
            log('→ Showing status card and hiding form');
            showStatusCard(submission);
          } else {
            log('→ Hiding form and status (disabled)');
            hideFormAndStatus();
          }
        }, 3000);

      } else {
        throw new Error(result.message || result.error || `Server returned status ${response.status}`);
      }

    } catch (error) {
      logError('✗ Form submission error:', error);

      let errorMessage = 'There was an error submitting your request. Please try again.';

      if (error.name === 'AbortError') {
        errorMessage = 'The request timed out. Please check your internet connection and try again.';
      } else if (error.message.includes('Failed to fetch') || error.name === 'TypeError') {
        errorMessage = 'Unable to connect to the server. Please check your internet connection and try again.';
      } else if (error.message.includes('400')) {
        errorMessage = 'There was a validation error with your form data. Please check your information and try again.';
      } else if (error.message.includes('404')) {
        errorMessage = 'Form submission endpoint not found. Please contact support.';
      } else if (error.message.includes('500') || error.message.includes('502') || error.message.includes('503')) {
        errorMessage = 'Server error. Please try again in a few moments.';
      }

      removeAllModals();
      showErrorModal(errorMessage);

    } finally {
      state.isSubmitting = false;
      unlockSubmitButton();
    }
  }

  function setupCharacterCounter() {
    const textarea = state.form.querySelector('textarea[name="reason"]');
    if (textarea) {
      const counter = state.form.querySelector('.character-count');
      if (counter) {
        textarea.addEventListener('input', function() {
          const length = this.value.length;
          counter.textContent = `${length}/2000`;

          if (length > 1900) {
            counter.style.color = '#dc3545';
          } else if (length > 1700) {
            counter.style.color = '#ffc107';
          } else {
            counter.style.color = '#919495';
          }
        });
      }
    }
  }

  function setupFieldValidation() {
    ['email', 'reason', 'terms_acknowledged'].forEach(fieldName => {
      const field = state.form.querySelector(`[name="${fieldName}"]`);
      if (field) {
        const clearError = () => removeFieldError(fieldName);
        field.addEventListener('input', clearError);
        field.addEventListener('change', clearError);
        field.addEventListener('focus', clearError);
      }
    });
  }

  function setupCheckboxListener() {
    const termsCheckbox = state.form.querySelector('input[name="terms_acknowledged"]');
    if (termsCheckbox) {
      termsCheckbox.addEventListener('change', updateSubmitButtonState);
      updateSubmitButtonState();
    }
  }

  // ========================================
  // INITIALIZATION
  // ========================================

  async function initializeForm() {
    state.form = document.querySelector('.deletion-form__form');

    if (!state.form) {
      setTimeout(initializeForm, 100);
      return;
    }

    // Set debug mode FIRST before any logging
    state.debugMode = state.form.dataset.debugMode === 'true';

    log('=== INITIALIZING DELETION FORM ===');
    log('Debug mode:', state.debugMode);

    state.showStatusEnabled = state.form.dataset.showStatus === 'true';
    log('Show status enabled:', state.showStatusEnabled);

    // Cache submit button reference
    state.submitButton = state.form.querySelector('button[type="submit"]');
    if (state.submitButton) {
      state.originalButtonText = state.submitButton.textContent;
    }

    const customerEmail = state.form.dataset.customerEmail;
    log('Customer email:', customerEmail);

    if (customerEmail) {
      log('→ Checking backend API for existing request...');
      showCheckingLoader();
      lockSubmitButton('Checking...');

      try {
        const existingSubmission = await checkForExistingRequest(customerEmail);
        
        log('→ Check result:', existingSubmission);
        
        hideCheckingLoader();

        if (existingSubmission && existingSubmission.exists) {
          log('✓ Existing submission found');
          state.hasExistingRequest = true;
          
          if (state.showStatusEnabled) {
            log('→ Showing status card');
            showStatusCard(existingSubmission);
          } else {
            log('→ Hiding everything');
            hideFormAndStatus();
          }
          
          return;
        } else {
          log('✓ No existing submission - showing form');
          unlockSubmitButton();
        }
      } catch (error) {
        logError('✗ Error during check:', error);
        hideCheckingLoader();
        unlockSubmitButton();
      }
    } else {
      log('→ No customer email available');
    }

    log('→ Setting up form...');
    state.form.addEventListener('submit', handleFormSubmit);
    setupCharacterCounter();
    setupFieldValidation();
    setupCheckboxListener();
    log('✓ Form initialized');
  }

  // Start initialization
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeForm);
  } else {
    initializeForm();
  }

})();
